import { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsFocused } from '@react-navigation/native';

import { filterPopularMarkets, isMatchupCardWinner } from '@/components/sbk-traditional-matchup-card/utils';
import { useEventKeys } from '@/feature/event-details-sbk/hooks/use-event';
import { useEventMarketCategories } from '@/feature/event-details-sbk/hooks/use-event-market-categories';
import {
    useHasPublishedMarkets,
    useInitialExpandedMarketAccordions,
} from '@/feature/event-details-sbk/hooks/use-market-cache';
import {
    useMicroMarketCacheStore,
    useMicroMarketOrder,
} from '@/feature/event-details-sbk/hooks/use-micro-market-cache';
import { useSubscribeEventMarketUpdate } from '@/feature/event-details-sbk/hooks/use-subscribe-event-market-update';
import { useSubscribeEventNewMarket } from '@/feature/event-details-sbk/hooks/use-subscribe-event-new-market';
import { useSubscribeEventOddsUpdate } from '@/feature/event-details-sbk/hooks/use-subscribe-event-odds-update';
import { EventWithMarkets, MarketCategory, MarketCategoryType } from '@/feature/event-details-sbk/types';
import { getMarketCategoryKey } from '@/feature/sbk-event-sheets/utils/get-market-category-key';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { getSportKey } from '@/utils/get-sport-key';
import { useQueryClient } from '@tanstack/react-query';

import { useEvent } from '../../event-details-sbk/hooks/use-event';

const MICRO_MARKET_CATEGORIES = ['play_by_play', 'pitch_by_pitch', 'plate_appearance', 'drive'];

// Returns all the necessary data for the micro market tab
const useMicroMarketContent = (
    eventId: string,
    marketCategoryId: string,
    marketCategoryType: MarketCategoryType,
    enabled: boolean
) => {
    useSubscribeEventNewMarket(eventId, marketCategoryId, marketCategoryType, enabled);
    const {
        data,
        isLoading: isMicroMarketsLoading,
        isFetched,
    } = useEventMarketCategories(eventId, marketCategoryId, enabled, {
        refetchInterval: 0,
    });
    const populate = useMicroMarketCacheStore(state => state.actions.populate);
    useEffect(() => {
        if (data) {
            populate(data, eventId, marketCategoryType);
        }
    }, [populate, data, eventId, marketCategoryType]);

    const microMarketOrder = useMicroMarketOrder(eventId, marketCategoryType);

    return {
        isFetched,
        hasPublishedMarkets: microMarketOrder.length > 0,
        microMarketOrder,
        isMicroMarketsLoading,
    };
};

// Returns all the necessary data for the player props tab
const usePlayerPropsContent = (eventId: string, marketCategoryId: string, enabled: boolean) => {
    const { data, isLoading, isFetched } = useEventMarketCategories(eventId, marketCategoryId, enabled, {
        refetchInterval: 0,
    });

    const [selectedMarketCategoryKey, setSelectedMarketCategoryKey] = useState<string>();

    const selectedMarketCategory =
        data?.find(category => getMarketCategoryKey(category) === selectedMarketCategoryKey) ?? data?.[0];

    const updateSelectedMarketCategory = useCallback((category: MarketCategory) => {
        setSelectedMarketCategoryKey(getMarketCategoryKey(category));
    }, []);

    const hasPublishedMarkets = useHasPublishedMarkets(data ?? []);

    return {
        hasPublishedMarkets,
        isFetched,
        playerProps: data,
        selectedMarketCategory,
        updateSelectedMarketCategory,
        isPlayerPropsLoading: isLoading,
    };
};

// Returns all the necessary data for the market accordion tabs (game_lines, half_props, quarter_props, etc.)
const useMarketAccordionContent = (eventId: string, marketCategoryId: string, enabled: boolean, sportName?: string) => {
    useSubscribeEventMarketUpdate(eventId, marketCategoryId, enabled);
    const { data = [], isLoading, isFetched } = useEventMarketCategories(eventId, marketCategoryId, enabled);

    const filteredMarketCategories = useMemo(() => {
        // Drop the matchup-card winner only — spread/total accordions are the alt-lines entry point.
        return data.filter((marketCategory: MarketCategory) => !isMatchupCardWinner(marketCategory, sportName));
    }, [data, sportName]);

    const initialExpandedMarkets = useInitialExpandedMarketAccordions(filteredMarketCategories);

    const hasPublishedMarkets = useHasPublishedMarkets(data);

    return {
        isFetched,
        hasPublishedMarkets,
        initialExpandedMarkets,
        filteredMarketCategories,
        marketCategories: data,
        isMarketAccordionLoading: isLoading,
    };
};

type MarketLayoutType = 'micro_market' | 'player_props' | 'market_accordion';

// This hook returns all the necessary data for the event sheet screen.
// Depending on which tab is selected, the hook will return the list if items to render
// The different types of tabs are:
// - market_accordion: market accordions
// - player_props: player props
// - micro_market: micro markets
//
// The content for each tab is within their own hooks
// - micro_market: useMicroMarketContent
// - player_props: usePlayerPropsContent
// - market_accordion: useMarketAccordionContent
//
// Each tab has different data to render (itemsToRender):
// - market_accordion: tabs, matchup_card, list of market accordions (MarketCategory objects)
// - player_props: tabs, list of player props markets (Market objects)
// - micro_market: tabs, list of micro market ids (string)

export const useEventContent = ({ eventId, isActive }: { eventId: string; isActive: boolean }) => {
    const { data: event } = useEvent(eventId);
    const { t } = useTranslation('market_categories');

    // Market Tabs
    const tabs = useMemo(() => {
        return (
            event?.market_categories?.map(category => {
                const sportNamespace = getSportKey(event.sport);
                const key = `${sportNamespace}.${category.type}`;
                const title = t(key, { defaultValue: t(category.type) }) as string;
                return {
                    id: category.id,
                    key: category.type,
                    title,
                };
            }) ?? []
        );
    }, [event?.market_categories, event?.sport, t]);

    const [selectedTab, setSelectedTab] = useState<(typeof tabs)[0] | undefined>(tabs[0]);

    // Set the default selected tab to the first tab
    useEffect(() => {
        if (tabs.length > 0 && !selectedTab) {
            setSelectedTab(tabs[0]);
        }
    }, [tabs, selectedTab]);

    // Update the selected tab when a tab is clicked
    const updateSelectedTab = useCallback(
        (tab: string) => {
            setSelectedTab(tabs.find(item => item.id === tab) ?? tabs[0]);
        },
        [tabs]
    );

    // Get the selected market category id and type
    const selectedMarketCategoryId = selectedTab?.id ?? '';
    const selectedMarketCategoryType: MarketCategoryType = selectedTab?.key ?? 'game_lines';
    const isFocused = useIsFocused();
    const enabled = !!selectedTab && isActive && isFocused;

    // Subscribe to odds updates for the selected market category
    useSubscribeEventOddsUpdate(eventId, selectedMarketCategoryId, enabled);

    // Determine the market layout type based on the selected tab
    const marketLayoutType: MarketLayoutType = useMemo(() => {
        if (selectedTab?.key && MICRO_MARKET_CATEGORIES.includes(selectedTab.key)) {
            return 'micro_market';
        }
        if (selectedTab?.key && selectedTab.key === 'player_props') {
            return 'player_props';
        }
        return 'market_accordion';
    }, [selectedTab]);

    const {
        isFetched: isMarketAccordionFetched,
        initialExpandedMarkets,
        filteredMarketCategories,
        marketCategories,
        isMarketAccordionLoading,
        hasPublishedMarkets: hasPublishedMarketAccordionMarkets,
    } = useMarketAccordionContent(
        eventId,
        selectedMarketCategoryId,
        enabled && marketLayoutType === 'market_accordion',
        event?.sport.name
    );

    const {
        isFetched: isMicroMarketsFetched,
        microMarketOrder,
        isMicroMarketsLoading,
        hasPublishedMarkets: hasPublishedMicroMarkets,
    } = useMicroMarketContent(
        eventId,
        selectedMarketCategoryId,
        selectedMarketCategoryType,
        enabled && marketLayoutType === 'micro_market'
    );

    const {
        isFetched: isPlayerPropsFetched,
        selectedMarketCategory,
        updateSelectedMarketCategory,
        isPlayerPropsLoading,
        hasPublishedMarkets: hasPublishedPlayerPropsMarkets,
    } = usePlayerPropsContent(eventId, selectedMarketCategoryId, enabled && marketLayoutType === 'player_props');

    // Get the items to render based on the market layout type
    const itemsToRender = useMemo(() => {
        if (marketLayoutType === 'micro_market') {
            return ['tabs', ...microMarketOrder];
        }
        if (marketLayoutType === 'player_props') {
            const markets = selectedMarketCategory?.markets ?? [];
            return ['tabs', ...markets];
        }
        return [
            'tabs',
            selectedTab?.key === 'game_lines' ? 'matchup_card' : undefined,
            ...filteredMarketCategories,
        ].filter(Boolean);
    }, [selectedTab, filteredMarketCategories, microMarketOrder, selectedMarketCategory, marketLayoutType]);

    // Determines if there are any published markets for the selected tab
    const hasPublishedMarkets = useMemo(() => {
        if (marketLayoutType === 'micro_market') {
            return hasPublishedMicroMarkets;
        }
        if (marketLayoutType === 'player_props') {
            return hasPublishedPlayerPropsMarkets;
        }
        return hasPublishedMarketAccordionMarkets;
    }, [
        hasPublishedMicroMarkets,
        hasPublishedPlayerPropsMarkets,
        hasPublishedMarketAccordionMarkets,
        marketLayoutType,
    ]);

    // Determines if the data for the selected tab is fetched
    const isFetched = useMemo(() => {
        if (marketLayoutType === 'micro_market') {
            return isMicroMarketsFetched;
        }
        if (marketLayoutType === 'player_props') {
            return isPlayerPropsFetched;
        }
        return isMarketAccordionFetched;
    }, [isMicroMarketsFetched, isPlayerPropsFetched, isMarketAccordionFetched, marketLayoutType]);

    // Refetchs the current selected market category when returning from background
    const queryClient = useQueryClient();
    const eventKeys = useEventKeys();
    const refetch = useCallback(async () => {
        queryClient.refetchQueries({ queryKey: eventKeys.marketCategories(eventId, selectedMarketCategoryId) });
    }, [queryClient, eventKeys, eventId, selectedMarketCategoryId]);
    useFetchOnResume(refetch);

    // TODO: potentially move this logic to the BE endpoint
    const eventWithPopularMarkets: EventWithMarkets | null = useMemo(() => {
        if (!event) {
            return null;
        }
        return {
            ...event,
            popular_markets: marketCategories.filter(mc => filterPopularMarkets(mc, event.sport.name)),
        };
    }, [event, marketCategories]);

    return {
        tabs,
        event,
        eventWithPopularMarkets,
        updateSelectedTab,
        selectedTab,
        marketCategories,
        itemsToRender,
        marketLayoutType,
        initialExpandedMarkets,
        isFetched,
        isLoading: isMicroMarketsLoading || isPlayerPropsLoading || isMarketAccordionLoading,
        hasPublishedMarkets,
        // Player props specific data
        selectedMarketCategory,
        updateSelectedMarketCategory,
    };
};
